import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Wand2, Sun, Contrast, Droplets, Focus, Thermometer, Sparkles,
  Check, Loader2, ArrowLeft, ArrowRight, ZoomIn, ZoomOut,
  Paintbrush, SkipForward, CheckSquare, Square,
  RotateCcw, Maximize2, Hand,
} from 'lucide-react';

const BACKEND_URL = RUNTIME_BACKEND_URL;

const TOOLS = [
  { id: 'spot_heal', label: 'Leke Temizle', icon: Wand2 },
  { id: 'smooth', label: 'Yumusatma', icon: Paintbrush },
];

const FILTERS = [
  { key: 'brightness', label: 'Parlaklik', icon: Sun, min: 0, max: 100, step: 1, def: 50 },
  { key: 'contrast', label: 'Kontrast', icon: Contrast, min: 0, max: 100, step: 1, def: 50 },
  { key: 'saturation', label: 'Doygunluk', icon: Droplets, min: 0, max: 100, step: 1, def: 50 },
  { key: 'sharpness', label: 'Keskinlik', icon: Focus, min: 0, max: 100, step: 1, def: 50 },
  { key: 'warmth', label: 'Sicaklik', icon: Thermometer, min: 0, max: 100, step: 1, def: 50 },
  { key: 'skin_smooth', label: 'Cilt', icon: Sparkles, min: 0, max: 100, step: 1, def: 0 },
];

const DEFAULT_SETTINGS = { brightness: 50, contrast: 50, saturation: 50, sharpness: 50, warmth: 50, skin_smooth: 0 };

export default function PhotoRetouchEditor({ photos, albumId, token, onComplete, onSkip, onBack }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [activeTool, setActiveTool] = useState('spot_heal');
  const [brushSize, setBrushSize] = useState(25);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);
  const [retouchedSet, setRetouchedSet] = useState(new Set());
  const [batchSelected, setBatchSelected] = useState(new Set());
  const [clickMarkers, setClickMarkers] = useState([]);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0, visible: false });

  // Zoom & Pan
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panMode, setPanMode] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  // Filtreler
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });

  // CSS filtre string - canli onizleme (sadece brightness, contrast, saturation)
  const getCssFilter = () => {
    const b = settings.brightness / 50;
    const c = settings.contrast / 50;
    const s = settings.saturation / 50;
    const warmth = (settings.warmth - 50) / 50;
    const sepia = warmth > 0 ? warmth * 0.25 : 0;
    const hue = warmth * -10;
    return `brightness(${b}) contrast(${c}) saturate(${s}) sepia(${sepia}) hue-rotate(${hue}deg)`;
  };

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const currentPhoto = photos[selectedIdx];

  // Alt + Scroll: FARE KONUMUNDA yakinlastirma
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      if (!e.altKey) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      setScale(prev => {
        const factor = e.deltaY < 0 ? 1.2 : 0.83;
        const next = Math.min(15, Math.max(0.5, prev * factor));
        const ratio = next / prev;
        setTranslate(t => ({
          x: mx - ratio * (mx - t.x),
          y: my - ratio * (my - t.y)
        }));
        return next;
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Mouse tracker - firca boyutunu goster
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const move = (e) => {
      const rect = el.getBoundingClientRect();
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top, visible: true });
    };
    const leave = () => setMousePos(p => ({ ...p, visible: false }));
    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    return () => { el.removeEventListener('mousemove', move); el.removeEventListener('mouseleave', leave); };
  }, []);

  // Pan handlers (pointer events)
  const onPointerDown = (e) => {
    if (!panMode) return;
    e.preventDefault();
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, tx: translate.x, ty: translate.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!isPanning) return;
    setTranslate({
      x: panStartRef.current.tx + (e.clientX - panStartRef.current.x),
      y: panStartRef.current.ty + (e.clientY - panStartRef.current.y)
    });
  };
  const onPointerUp = (e) => {
    if (!isPanning) return;
    setIsPanning(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  // Spot heal click
  const handleCanvasClick = useCallback(async (e) => {
    if (!currentPhoto || isProcessing || panMode) return;
    const img = imgRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const origX = Math.round((e.clientX - rect.left) * scaleX);
    const origY = Math.round((e.clientY - rect.top) * scaleY);
    const radius = Math.round(brushSize * scaleX);

    // Anlik marker
    const markerId = Date.now();
    setClickMarkers(prev => [...prev, {
      id: markerId,
      left: ((e.clientX - rect.left) / rect.width * 100),
      top: ((e.clientY - rect.top) / rect.height * 100),
    }]);
    setTimeout(() => setClickMarkers(prev => prev.filter(m => m.id !== markerId)), 800);

    setIsProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/spot-heal`, {
        method: 'POST', headers,
        body: JSON.stringify({ photo_id: currentPhoto.id, x: origX, y: origY, radius, tool: activeTool })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Hata (${res.status})`);
      }
      setImgVersion(v => v + 1);
      setRetouchedSet(prev => new Set(prev).add(currentPhoto.id));
    } catch (err) { toast.error(err.message); }
    finally { setIsProcessing(false); }
  }, [currentPhoto, isProcessing, panMode, brushSize, albumId, activeTool, token]);

  // Filtre uygula
  const applyFilters = async () => {
    let ids = Array.from(batchSelected);
    if (ids.length === 0 && currentPhoto) ids = [currentPhoto.id];
    if (ids.length === 0) return;
    const hasChanges = FILTERS.some(f => settings[f.key] !== f.def);
    if (!hasChanges) { toast.error('En az bir filtre degistirin'); return; }
    setIsBatchProcessing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${albumId}/retouch`, {
        method: 'POST', headers,
        body: JSON.stringify({ photo_ids: ids, settings })
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `Hata (${res.status})`);
      }
      const data = await res.json();
      toast.success(`${data.processed} fotograf islendi`);
      ids.forEach(id => setRetouchedSet(prev => new Set(prev).add(id)));
      setImgVersion(v => v + 1);
      setSettings({ ...DEFAULT_SETTINGS });
    } catch (err) { toast.error(err.message); }
    finally { setIsBatchProcessing(false); }
  };

  const resetPhoto = async () => {
    if (!currentPhoto) return;
    setIsProcessing(true);
    try {
      await fetch(`${BACKEND_URL}/api/albums/${albumId}/retouch/reset`, {
        method: 'POST', headers,
        body: JSON.stringify({ photo_ids: [currentPhoto.id] })
      });
      setRetouchedSet(prev => { const n = new Set(prev); n.delete(currentPhoto.id); return n; });
      setImgVersion(v => v + 1);
      setSettings({ ...DEFAULT_SETTINGS });
      toast.success('Orijinale donuldu');
    } catch { toast.error('Sifirlama basarisiz'); }
    finally { setIsProcessing(false); }
  };

  // Tam ekran / sifirla
  const resetView = () => { setScale(1); setTranslate({ x: 0, y: 0 }); };
  const fitScreen = () => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;
    const cRect = container.getBoundingClientRect();
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const fitScale = Math.min((cRect.width * 0.9) / iw, (cRect.height * 0.9) / ih, 1);
    setScale(fitScale);
    setTranslate({ x: (cRect.width - iw * fitScale) / 2, y: (cRect.height - ih * fitScale) / 2 });
  };

  const toggleBatchAll = () => {
    if (batchSelected.size === photos.length) setBatchSelected(new Set());
    else setBatchSelected(new Set(photos.map(p => p.id)));
  };
  const toggleBatch = (id) => {
    setBatchSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  // Foto degisince sifirla
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setSettings({ ...DEFAULT_SETTINGS });
  }, [selectedIdx]);

  // Brush size indicator (pixel on screen)
  const brushDisplaySize = brushSize * 2 * scale;

  return (
    <div className="flex flex-col h-full" data-testid="retouch-editor">
      <div className="flex flex-1 overflow-hidden">

        {/* MERKEZ - Fotograf */}
        <div className="flex-1 flex flex-col overflow-hidden bg-[#1a1a2e]">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-3 py-1.5 bg-[#16162a] border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white h-7 text-xs"
                onClick={() => setScale(s => Math.max(0.3, s * 0.75))}><ZoomOut className="h-3.5 w-3.5" /></Button>
              <span className="text-xs font-mono text-slate-400 w-14 text-center">{Math.round(scale * 100)}%</span>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white h-7 text-xs"
                onClick={() => setScale(s => Math.min(15, s * 1.3))}><ZoomIn className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white h-7 text-xs"
                onClick={fitScreen} title="Ekrana Sigdir"><Maximize2 className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white h-7 text-xs"
                onClick={resetView} title="%100">1:1</Button>
              <div className="w-px h-5 bg-slate-600 mx-1" />
              <Button variant="ghost" size="sm"
                className={`h-7 text-xs ${panMode ? 'bg-purple-600 text-white' : 'text-slate-300 hover:text-white'}`}
                onClick={() => setPanMode(!panMode)} title="El Araci">
                <Hand className="h-3.5 w-3.5 mr-1" /> El
              </Button>
              <div className="w-px h-5 bg-slate-600 mx-1" />
              <span className="text-[10px] text-slate-500">Firca: {brushSize}px</span>
            </div>
            <span className="text-xs text-slate-400">{selectedIdx + 1} / {photos.length}
              {isProcessing && <Loader2 className="inline h-3.5 w-3.5 animate-spin text-purple-400 ml-2" />}
            </span>
            <div className="flex items-center gap-1">
              {retouchedSet.has(currentPhoto?.id) && (
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 h-7 text-xs"
                  onClick={resetPhoto}><RotateCcw className="h-3.5 w-3.5 mr-1" />Geri Al</Button>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div ref={containerRef}
            className="flex-1 overflow-hidden relative"
            style={{ cursor: panMode ? (isPanning ? 'grabbing' : 'grab') : 'none' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}>

            {currentPhoto && (
              <div style={{
                position: 'absolute',
                left: translate.x,
                top: translate.y,
                transform: `scale(${scale})`,
                transformOrigin: '0 0',
              }}>
                <img
                  ref={imgRef}
                  src={`${BACKEND_URL}/api/albums/${albumId}/photos/${currentPhoto.id}/retouched?v=${imgVersion}`}
                  alt=""
                  className="block shadow-2xl select-none"
                  style={{ filter: getCssFilter(), maxWidth: 'none' }}
                  draggable={false}
                  onClick={panMode ? undefined : handleCanvasClick}
                  onLoad={() => {
                    // Ilk yuklemede ekrana sigdir
                    if (scale === 1 && translate.x === 0 && translate.y === 0) {
                      setTimeout(fitScreen, 50);
                    }
                  }}
                  data-testid="retouch-canvas-img"
                />
                {/* Click markers */}
                {clickMarkers.map(m => (
                  <div key={m.id} className="absolute pointer-events-none rounded-full"
                    style={{
                      left: `${m.left}%`, top: `${m.top}%`,
                      width: brushSize * 2, height: brushSize * 2,
                      marginLeft: -brushSize, marginTop: -brushSize,
                      border: '2px solid rgba(168,85,247,0.9)',
                      animation: 'retouchPing 0.7s ease-out forwards'
                    }} />
                ))}
              </div>
            )}

            {/* FIRCA BOYUTU GOSTERGESI - dairesel cursor */}
            {!panMode && mousePos.visible && (
              <div className="pointer-events-none fixed z-50 rounded-full"
                style={{
                  left: containerRef.current?.getBoundingClientRect().left + mousePos.x,
                  top: containerRef.current?.getBoundingClientRect().top + mousePos.y,
                  width: brushDisplaySize,
                  height: brushDisplaySize,
                  transform: 'translate(-50%, -50%)',
                  border: '1.5px solid rgba(168,85,247,0.8)',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                }} />
            )}
          </div>
        </div>

        {/* SAG PANEL */}
        <div className="w-56 bg-[#1e1e2e] border-l border-slate-700 flex flex-col overflow-y-auto flex-shrink-0">
          {/* Araclar */}
          <div className="p-3 border-b border-slate-700">
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Araclar</p>
            <div className="flex gap-1">
              {TOOLS.map(tool => (
                <button key={tool.id} onClick={() => { setActiveTool(tool.id); setPanMode(false); }}
                  className={`flex-1 flex flex-col items-center gap-1 px-2 py-2 rounded text-[10px] transition-colors ${
                    activeTool === tool.id && !panMode ? 'bg-purple-600 text-white' : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                  }`} data-testid={`tool-${tool.id}`}>
                  <tool.icon className="h-4 w-4" />
                  {tool.label}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <div className="flex justify-between text-[10px] text-slate-500 mb-0.5">
                <span>Firca Boyutu</span><span className="font-mono text-purple-400">{brushSize}px</span>
              </div>
              <input type="range" min={5} max={120} value={brushSize}
                onChange={e => setBrushSize(parseInt(e.target.value))}
                className="w-full h-2 accent-purple-500" data-testid="brush-size-slider" />
            </div>
          </div>

          {/* Filtreler */}
          <div className="p-3 border-b border-slate-700">
            <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Filtreler (Canli)</p>
            {FILTERS.map(({ key, label, icon: Icon, min, max, step: s }) => (
              <div key={key} className="mb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Icon className="h-3 w-3" /> {label}
                  </span>
                  <span className="text-[10px] font-mono text-slate-500">{settings[key]}</span>
                </div>
                <input type="range" min={min} max={max} step={s} value={settings[key]}
                  onChange={e => setSettings(prev => ({ ...prev, [key]: parseInt(e.target.value) }))}
                  className="w-full h-1.5 accent-purple-500" data-testid={`filter-${key}`} />
              </div>
            ))}
            <div className="flex gap-1 mt-2">
              <Button variant="ghost" size="sm"
                className="flex-1 text-[10px] text-slate-400 h-7 hover:text-white"
                onClick={() => setSettings({ ...DEFAULT_SETTINGS })}>Sifirla</Button>
              <Button size="sm"
                className="flex-1 text-[10px] bg-purple-600 hover:bg-purple-700 text-white h-7"
                disabled={isBatchProcessing} onClick={applyFilters} data-testid="apply-filters-btn">
                {isBatchProcessing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Kaydet'}
              </Button>
            </div>
            <p className="text-[9px] text-slate-600 mt-1">
              {batchSelected.size > 0 ? `${batchSelected.size} foto secili` : 'Aktif fotografa uygulanir'}
            </p>
          </div>

          {/* Toplu */}
          <div className="p-3">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold text-slate-500 uppercase">Toplu</p>
              <button onClick={toggleBatchAll} className="text-[10px] text-purple-400 hover:text-purple-300">
                {batchSelected.size === photos.length ? 'Kaldir' : 'Hepsini Sec'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ALT - Thumbnails */}
      <div className="bg-[#12121f] border-t border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-1 px-2 py-1.5">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-400 hover:text-white h-7 text-xs flex-shrink-0">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Geri
          </Button>

          <div className="flex-1 overflow-x-auto flex items-center gap-1 px-2">
            {photos.map((photo, idx) => {
              const isActive = idx === selectedIdx;
              const isRetouched = retouchedSet.has(photo.id);
              const isBatch = batchSelected.has(photo.id);
              return (
                <div key={photo.id}
                  onClick={() => setSelectedIdx(idx)}
                  className={`relative flex-shrink-0 w-14 h-14 rounded overflow-hidden cursor-pointer border-2 transition-all ${
                    isActive ? 'border-purple-500 ring-1 ring-purple-400' :
                    isBatch ? 'border-blue-400' :
                    isRetouched ? 'border-green-500' : 'border-transparent hover:border-slate-500'
                  }`} data-testid={`thumb-${photo.id}`}>
                  <img src={`${BACKEND_URL}/api/albums/${albumId}/photos/${photo.id}/thumbnail`}
                    alt="" className="w-full h-full object-cover" />
                  {isRetouched && (
                    <div className="absolute top-0 right-0 bg-green-600 rounded-bl w-3 h-3 flex items-center justify-center">
                      <Sparkles className="h-2 w-2 text-white" />
                    </div>
                  )}
                  <button className="absolute bottom-0 right-0 bg-black/50 rounded-tl w-4 h-4 flex items-center justify-center hover:bg-purple-600"
                    onClick={(e) => { e.stopPropagation(); toggleBatch(photo.id); }}>
                    {isBatch ? <CheckSquare className="h-2.5 w-2.5 text-white" /> : <Square className="h-2.5 w-2.5 text-white" />}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={onSkip} className="text-slate-400 hover:text-white h-7 text-xs" data-testid="skip-retouch">
              <SkipForward className="h-3.5 w-3.5 mr-1" /> Atla
            </Button>
            <Button size="sm" onClick={onComplete} className="bg-purple-600 hover:bg-purple-700 h-7 text-xs" data-testid="retouch-continue">
              Devam <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes retouchPing {
          0% { opacity:1; transform:scale(1); }
          100% { opacity:0; transform:scale(2); }
        }
      `}</style>
    </div>
  );
}
